// Per-airline-program editorial insight surfaced on every live flight
// result. Hand-curated, intentionally opinionated — designed to give the
// user the "what's actually good/bad about booking through this program"
// take they'd get from a knowledgeable friend.
//
// `program` key matches the seats.aero Source field, lowercased.

// Each insight has a `transfers` array describing how to MOVE points TO
// this airline program from your transferable currencies. Format:
//   { source: 'amex_mr', ratio: '1:1', time: 'Instant', notes?: '...' }
// The keys match the source_program values in the backend transfer_partners
// table so a UI can look up the transfer-portal URL via bookingLinks.

export const PROGRAM_INSIGHTS = {
  aeroplan: {
    name: 'Air Canada Aeroplan',
    sweetSpot: 'Star Alliance redemptions to Europe — predictable distance pricing, NO fuel surcharges on most partners (Lufthansa is the rare exception).',
    pros: [
      'Transferable from BOTH Amex MR and Chase UR 1:1 — easy to top up from either side',
      'Free stopovers (5,000 pts each) — turn a single award into a 2-city trip',
      'Generous family pooling and award change/cancel policies',
    ],
    cons: [
      'Award space tightens 60-90 days out — book early or pivot dates',
      'Fuel surcharges on Lufthansa metal can run $400+/person — avoid LH on award redemptions',
    ],
    productNotes: 'Air Canada\'s own Boeing 787 Signature Class is a solid 1-2-1 lie-flat with direct aisle access. Their 777-300ER business is older but still fully flat.',
    whyRecommend: 'Aeroplan is the easiest "first transfer" for anyone new to award travel — search is friendly, surcharges are predictable, and both major banks transfer instantly.',
    transfers: [
      { source: 'amex_mr', ratio: '1:1', time: 'Instant' },
      { source: 'chase_ur', ratio: '1:1', time: 'Instant' },
      { source: 'capital_one', ratio: '1:1', time: 'Instant' },
      { source: 'bilt', ratio: '1:1', time: 'Instant' },
    ],
    bookingUrl: 'https://www.aircanada.com/aeroplan/redeem',
    transferSteps: [
      'Confirm award seats on aircanada.com/aeroplan/redeem FIRST — never transfer before you see the seat',
      'Open your bank\'s transfer portal and select Aeroplan as the partner',
      'Transfer the exact number of points needed (instant)',
      'Refresh Aeroplan account — book the award the moment points land',
      'Pay the $112 CAD taxes per ticket at checkout',
    ],
  },
  lifemiles: {
    name: 'Avianca LifeMiles',
    sweetSpot: 'Star Alliance business to Europe for ~63K each way, NO fuel surcharges on any partner. The pure-points play.',
    pros: [
      'Lowest cash + lowest taxes of any major Star Alliance program for transatlantic business',
      'Frequent 15-30% transfer bonuses from Amex MR — wait for one if your dates are flexible',
      'Cash + miles option to top off small balance shortfalls',
    ],
    cons: [
      'Search engine is buggy — sometimes shows phantom space; verify by phone before transferring',
      'Customer service is the weakest of the big programs — keep your booking confirmation',
      'Amex MR exclusive (no Chase transfer path)',
    ],
    productNotes: 'You\'re booking partner metal (Lufthansa, Swiss, United, Turkish, ANA). Cabin quality depends entirely on which carrier — TK J on the 787 is excellent; UA Polaris is solid; LH old fleet is dated.',
    whyRecommend: 'Best value on points per seat for Star Alliance to Europe — if a bonus is live, this is hard to beat. Just confirm space twice.',
  },
  flyingblue: {
    name: 'Air France-KLM Flying Blue',
    sweetSpot: 'Monthly "Promo Rewards" that drop 25-50% pricing on specific routes. NYC ↔ Paris frequently shows up.',
    pros: [
      'Transfers from Amex MR, Chase UR, Citi, Capital One, AND Bilt — most flexible currency',
      'Dynamic pricing means cheap awards exist in shoulder seasons',
      'Easy online booking + change',
    ],
    cons: [
      'Award prices change frequently — what you saw yesterday may not be there today',
      'High taxes on KL/AF metal (~$300-500 per ticket for transatlantic business)',
      'Phantom availability bug is real — book promptly when you see space',
    ],
    productNotes: 'Air France new business is excellent — 1-2-1, lie-flat, attentive crew, top-tier wine. KLM business is solid but not premium-feeling. Skip the older AF 777s that haven\'t been refit.',
    whyRecommend: 'When a Promo Reward matches your dates, Flying Blue is often the absolute cheapest transatlantic business option from anywhere in the US.',
  },
  iberia: {
    name: 'Iberia Plus Avios',
    sweetSpot: 'Off-peak business class JFK/BOS/MIA → Madrid for 34,000 Avios each way. Best deal to Spain from the East Coast, period.',
    pros: [
      'Lowest pure-points cost for transatlantic business of any major program',
      'Transferable from Amex MR AND Chase UR 1:1',
      'IB business on A350 is a genuinely premium product',
    ],
    cons: [
      'Off-peak windows are restrictive — Sep is usually off-peak but verify by date',
      'Phone bookings often required (Iberia\'s site struggles with award search)',
      'Limited US gateways (JFK, BOS, ORD, MIA, LAX, SFO)',
    ],
    productNotes: 'IB A350-900 "Business Plus" is 1-2-1 fully lie-flat. Newer 787-9 service is also good. Avoid the older A330 if your route still has them.',
    whyRecommend: 'If your trip includes Madrid as outbound or return city, this is the single best redemption on the list. 34K is a steal.',
  },
  ba: {
    name: 'British Airways Avios',
    sweetSpot: 'Off-peak BOS ↔ LON for 50K each way. Short partner hops (American domestic) under 1,000 miles for 9,000 Avios.',
    pros: [
      'Transferable from Amex MR, Chase UR, AND directly via the BA Visa card',
      'Distance-based award chart rewards short hops generously',
      'Easy online booking',
    ],
    cons: [
      'Fuel surcharges on BA metal are PUNISHING — $700+/person each way to LON',
      'AVOID BA-operated flights for award redemptions — use Iberia or AA metal instead',
      'Off-peak windows narrow as you approach the date',
    ],
    productNotes: 'BA\'s new Club Suite (787-9, A350, retrofit 777) is excellent. Old Club World on 747-400 (now retired) was awful — confirm your bird is post-refit.',
    whyRecommend: 'Useful for short partner hops within Europe (AA domestic, IB short-haul) — but skip for the main transatlantic leg because of fuel surcharges.',
  },
  united: {
    name: 'United MileagePlus',
    sweetSpot: 'Star Alliance partner space — book ANA, Lufthansa, Swiss, Turkish via United Excursionist Perk for free open-jaws.',
    pros: [
      'Chase UR exclusive — no Amex path',
      'No fuel surcharges (United absorbs them when you book on miles)',
      'Excursionist Perk: free intra-Europe one-way attached to a US ↔ Europe award',
    ],
    cons: [
      'Dynamic pricing for United-operated flights — saver award space is rare',
      'Phone agents charge a $25 fee to book partners that don\'t show online',
      'Bag fees + seat selection charges still apply on award tickets',
    ],
    productNotes: 'United Polaris (787, 777-300ER, some 767s) is genuinely good — 1-2-1, lie-flat, Saks Fifth Ave bedding. The older 757/767 Polaris-lite is dated.',
    whyRecommend: 'Best Chase UR option for Star Alliance partner space + the Excursionist Perk turns a single award into two segments.',
  },
  virginatlantic: {
    name: 'Virgin Atlantic Flying Club',
    sweetSpot: 'Delta One JFK ↔ LHR for 50K each way. ANA First/Business from US ↔ Tokyo (RT requirement, but absurd value).',
    pros: [
      'Transferable from Amex MR, Chase UR, AND Citi TY',
      'Frequent 30%+ transfer bonuses from Amex',
      'Books Delta partner space Delta\'s own program won\'t show',
    ],
    cons: [
      'High fuel surcharges on Virgin\'s own metal',
      'Phone-only for some partner bookings (ANA, China Eastern)',
      'Round-trip required for ANA awards (and no one-ways on partners in general)',
    ],
    productNotes: 'Delta One (A330, A350, 767) is solid 1-2-1 lie-flat. Virgin\'s own Upper Class on A350 is one of the world\'s best business cabins.',
    whyRecommend: 'For Delta partner space that Delta\'s own program hides, Virgin Atlantic is the workaround. For ANA F/J, it\'s the cheapest path from any major US currency.',
  },
  ana: {
    name: 'ANA Mileage Club',
    sweetSpot: 'Round-trip business US ↔ Tokyo for 75-90K (off-peak). Insanely good but Amex MR ONLY and RT required.',
    pros: [
      'Cheapest transpacific business award of any major program',
      'ANA\'s own The Room business class is arguably the world\'s best',
      'Family-pooling allowed once enrolled in their Award Recipient program',
    ],
    cons: [
      'Amex MR exclusive — no Chase path',
      'Transfers take 2-3 business days — you cannot react to last-minute award space',
      'Round-trip ONLY for partner awards — no one-ways',
      'Strict booking name policies — family members must be pre-registered',
    ],
    productNotes: 'ANA "The Room" on 777-300ER is the gold standard — fully-enclosed door suite, 2-meter bed, sliding privacy partition. 787 business is also good but smaller seat.',
    whyRecommend: 'If your trip involves Tokyo and you have Amex MR + 2-3 days buffer, this is the single best business-class redemption in the world.',
  },
  delta: {
    name: 'Delta SkyMiles',
    sweetSpot: 'Honestly? Skip it for award booking. Use Virgin Atlantic to book Delta partner space instead.',
    pros: [
      'No expiration on miles',
      'Easy online booking',
      'No fuel surcharges',
    ],
    cons: [
      'Pure dynamic pricing — same seat can be 75K or 400K depending on the day',
      'No published award chart — you can\'t plan ahead',
      'Amex MR transfer only (Chase has no path)',
    ],
    productNotes: 'Delta One is solid (see Virgin Atlantic note). Premium Select is a nice premium-economy.',
    whyRecommend: "We don't, generally — book the same Delta metal through Virgin Atlantic at a fixed 50K price instead.",
  },
  emirates: {
    name: 'Emirates Skywards',
    sweetSpot: 'EK metal in business — strong product, predictable pricing, but high YQ surcharges.',
    pros: [
      'Transferable from Amex MR, Chase UR, Citi TY, Capital One',
      'Award space on Emirates metal is generally good',
      'Mid-tier elite gives free Wi-Fi and lounge access',
    ],
    cons: [
      'Fuel surcharges run $500-1,200/person on long-hauls — eats into the points value',
      'Limited US gateways and only Emirates-operated routes',
      'Skywards miles expire (3 years from earn)',
    ],
    productNotes: 'EK A380 business has bar, shower (first only), 1-2-1 lie-flat. New 777 business is also 1-2-1. Old 777 business is 2-3-2 (avoid).',
    whyRecommend: 'Only if you specifically want EK metal and accept the surcharges. Otherwise skip.',
  },
  turkish: {
    name: 'Turkish Miles & Smiles',
    sweetSpot: '45K one-way business US ↔ Europe, NO fuel surcharges, transferable from Capital One, Citi, and Bilt.',
    pros: [
      'Lowest published award price for transatlantic business on Star Alliance, post-Aeroplan',
      'Multiple transfer paths (Cap One, Citi, Bilt — NOT Amex/Chase)',
      'Free stopover in Istanbul (great if you want a 2-day taste of the city)',
    ],
    cons: [
      'Phone booking required for most partner awards',
      'Customer service quality varies wildly',
      'Holds on awards can be inconsistent',
    ],
    productNotes: 'Turkish business on 787-9 and 777-300ER is excellent — 1-2-1, lie-flat, the best meal service in the sky (chef onboard).',
    whyRecommend: 'If you have Cap One Venture, Citi TY, or Bilt and Star Alliance + Europe is the goal, this is the best price-to-product ratio available.',
  },
};

// Transfer paths to each airline program from each transferable currency.
// Sourced to match the seed data in backend/src/db/seed-fn.js +
// seedExtraTransferPartners in backend/src/index.js. Surfaced inline on
// every flight result so the user always sees "here's exactly how to get
// the points to this program" without leaving the flow.
export const TRANSFER_PATHS = {
  aeroplan: [
    { source: 'amex_mr', ratio: '1:1', time: 'Instant' },
    { source: 'chase_ur', ratio: '1:1', time: 'Instant' },
    { source: 'capital_one', ratio: '1:1', time: 'Instant' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  lifemiles: [
    { source: 'amex_mr', ratio: '1:1', time: 'Instant' },
    { source: 'capital_one', ratio: '1:1', time: 'Instant' },
    { source: 'citi_ty', ratio: '1:1', time: 'Instant' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  flyingblue: [
    { source: 'amex_mr', ratio: '1:1', time: 'Instant' },
    { source: 'chase_ur', ratio: '1:1', time: 'Instant' },
    { source: 'citi_ty', ratio: '1:1', time: 'Instant' },
    { source: 'capital_one', ratio: '1:1', time: 'Instant' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  iberia: [
    { source: 'amex_mr', ratio: '1:1', time: 'Instant' },
    { source: 'chase_ur', ratio: '1:1', time: 'Instant' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  ba: [
    { source: 'amex_mr', ratio: '1:1', time: 'Instant' },
    { source: 'chase_ur', ratio: '1:1', time: 'Instant' },
    { source: 'capital_one', ratio: '1:1', time: 'Instant' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  united: [
    { source: 'chase_ur', ratio: '1:1', time: 'Instant', notes: 'Chase UR exclusive — no Amex path' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  virginatlantic: [
    { source: 'amex_mr', ratio: '1:1', time: 'Instant' },
    { source: 'chase_ur', ratio: '1:1', time: 'Instant' },
    { source: 'citi_ty', ratio: '1:1', time: 'Instant' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  ana: [
    { source: 'amex_mr', ratio: '1:1', time: '2-3 business days', notes: 'AMEX EXCLUSIVE. Round-trip required for partner awards.' },
  ],
  delta: [
    { source: 'amex_mr', ratio: '1:1', time: 'Instant', notes: 'Amex MR exclusive (no Chase path). Usually better to book Delta metal via Virgin Atlantic.' },
  ],
  emirates: [
    { source: 'amex_mr', ratio: '1:1', time: 'Instant' },
    { source: 'capital_one', ratio: '1:1', time: 'Instant' },
    { source: 'citi_ty', ratio: '1:1', time: 'Instant' },
    { source: 'bilt', ratio: '1:1', time: 'Instant' },
  ],
  turkish: [
    { source: 'capital_one', ratio: '1:1', time: 'Instant' },
    { source: 'citi_ty', ratio: '1:1', time: 'Instant' },
    { source: 'bilt', ratio: '1:1', time: 'Instant', notes: 'Not transferable from Amex MR or Chase UR' },
  ],
};

export const PROGRAM_BOOKING_URLS = {
  aeroplan: 'https://www.aircanada.com/aeroplan/redeem',
  lifemiles: 'https://www.lifemiles.com/',
  flyingblue: 'https://www.airfrance.us/',
  iberia: 'https://www.iberia.com/',
  ba: 'https://www.britishairways.com/travel/redeem',
  united: 'https://www.united.com/',
  virginatlantic: 'https://www.virginatlantic.com/',
  ana: 'https://www.ana.co.jp/en/us/',
  delta: 'https://www.delta.com/',
  emirates: 'https://www.emirates.com/',
  turkish: 'https://www.turkishairlines.com/',
};

// Generic per-step booking flow — used unless the program insight defines
// its own transferSteps override.
export function buildTransferSteps(programKey, bestSource = 'amex_mr') {
  const insight = PROGRAM_INSIGHTS[programKey];
  if (!insight) return [];
  const programName = insight.name;
  const sourceLabel = ({
    amex_mr: 'Amex MR', chase_ur: 'Chase UR', capital_one: 'Capital One Venture',
    citi_ty: 'Citi ThankYou', bilt: 'Bilt',
  })[bestSource] || bestSource;
  return [
    `Confirm the award seat is available on ${programName}'s site FIRST — never transfer before you see the seat`,
    `Open your ${sourceLabel} transfer portal and select ${programName} as the partner`,
    `Transfer the exact number of points needed (this is one-way and irreversible)`,
    `Wait for the transfer to land — most are instant; ANA takes 2-3 business days`,
    `Book the award immediately after points appear in your ${programName} account`,
    `Pay any taxes/fees at checkout — these vary by program (Aeroplan ~$112, BA via own metal $700+)`,
  ];
}

const SOURCE_ALIASES = {
  aeroplan: 'aeroplan',
  airfrance: 'flyingblue',
  flyingblue: 'flyingblue',
  ana: 'ana',
  ba: 'ba',
  britishairways: 'ba',
  delta: 'delta',
  emirates: 'emirates',
  iberia: 'iberia',
  lifemiles: 'lifemiles',
  united: 'united',
  virgin: 'virginatlantic',
  virginatlantic: 'virginatlantic',
  turkish: 'turkish',
  ms: 'turkish',
};

export function getInsightForSource(source) {
  if (!source) return null;
  const key = String(source).toLowerCase().replace(/[^a-z]/g, '');
  return PROGRAM_INSIGHTS[SOURCE_ALIASES[key] || key] || null;
}
